import { faClone, faCopy, faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Checkbox, CircularProgress, FormControl, FormControlLabel, TextField, Typography } from "@mui/material"
import { ALL_LOCALES, LOCALES } from "app/language/model"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { copyToClipboard } from "app/tools/clipboard"
import { deepEquals } from "app/tools/common"
import { ALL_CURRENCIES, CURRENCIES } from "currency/model"
import { getAvailableCurrencyIds } from "currency/tools/common"
import { FieldPhone } from "form/components/fields/FieldPhone"
import { FieldSelect } from "form/components/fields/FieldSelect"
import { FieldText } from "form/components/fields/FieldText"
import { ALL_MAP_DISTANCE_UNITS, DISTANCE_UNITS } from "form/map/model"
import { Form, Formik } from "formik"
import { messageSVC } from "message/service"
import React from "react"
import { connect } from "react-redux"
import { FlexBox } from "ui/components/FlexBox"
import { LinearLoader } from "ui/components/LinearLoader"
import { TestHiddenData } from "ui/components/TestHiddenData"
import { YesNotSelect } from "ui/components/YesNotSelect"
import { UI_THEMES } from "ui/model"
import { addImportant } from "ui/tools/common"
import { isTabletScreen } from "ui/tools/responsive"
import { getDefaultTheme } from "ui/tools/theme"
import {
  EMPTY_TOKEN_VALUE,
  IUser,
  IUserFormData,
  ROLE_NOT_FOUND_ERROR,
  USER_ROLE_ALREADY_EXISTS_ERROR,
  USER_ROLES
} from "user/model"
import { createUser, getDefaultUser, getUserAgency, getUserById, isValidPassword, updateUser } from "user/tools/common"
import { validateUserFormData } from "user/tools/form"
import {
  addDspRole,
  getUserRoles,
  isAppAdmin,
  isDspSale,
  isDspUser,
  isSspUser,
  isSspUserSession,
  removeDspRole,
  setUserAppAdmin
} from "user/tools/permissions"

interface IUserFormConnectProps {
  isInitializating: boolean
  isTablet: boolean
  isLoggedIn: boolean
  selfUser: IUser
}

interface IUserFormProps {
  otherUsers: IUser[]
  disableUserEdit?: boolean
  disableUserRole?: boolean
  disableUserPassword?: boolean
  userId: number | null // null = creation
  onSave: (user: IUser) => any
  onRoleChange?: (user: IUser) => any
  onCancel?: () => any
}

const UserFormFn = React.memo(
  ({
    selfUser,
    otherUsers,
    isInitializating,
    userId,
    disableUserEdit,
    disableUserPassword,
    disableUserRole,
    isTablet,
    isLoggedIn,
    onSave,
    onRoleChange,
    onCancel
  }: IUserFormProps & IUserFormConnectProps) => {
    const [initialUser, setInitialUser] = React.useState<null | IUserFormData>(null)
    const [userData, setUserData] = React.useState<null | IUserFormData>(null)
    const [initialIsAdmin, setInitialIsAdmin] = React.useState(false)
    const [isAdmin, setIsAdmin] = React.useState(false)
    const [isLoading, setIsLoading] = React.useState(false)
    const [isSaving, setIsSaving] = React.useState(false)
    const [password0, setPassword0] = React.useState("")
    const [password1, setPassword1] = React.useState("")
    const [password2, setPassword2] = React.useState("")
    const [refreshCount, setRefreshCount] = React.useState(0)
    const defaultDataRef = React.useRef<IUserFormData | null>(null)
    const passwordError: "" | "actual-invalid" | "different" | "invalid" = React.useMemo(() => {
      if (!password0 && !password1 && !password2) {
        return ""
      } else if (!password0 && password1) {
        return "actual-invalid"
      } else if (password1 !== password2) {
        return "different"
      } else if (!isValidPassword(password1)) {
        return "invalid"
      }
      return ""
    }, [password0, password1, password2])
    const [hasLoadingError, setHasLoadingError] = React.useState(false)
    const isCreation = React.useMemo(() => typeof userId !== "number", [userId])
    const hasUserChanged = React.useMemo(() => {
      if (isInitializating) {
        return false
      }
      if (initialUser === null) {
        return !deepEquals(userData, defaultDataRef.current)
      }
      return !deepEquals(userData, initialUser)
    }, [userData, isAdmin, initialUser, isInitializating])
    const hasAdminChanged = React.useMemo(() => isAdmin !== initialIsAdmin, [userData, isAdmin, initialIsAdmin])
    const isSelfUser = React.useMemo(() => selfUser.id === userId, [selfUser, userId])
    const hasRoleChanged = React.useMemo(() => initialUser?.role !== userData?.role, [initialUser, userData])
    const hasChanged = hasUserChanged || hasAdminChanged || password1 !== ""
    const areButtonsDisabled = disableUserEdit && disableUserPassword && disableUserRole
    React.useEffect(() => {
      try {
        if (!isLoggedIn || isInitializating) {
          return
        }
        const agency = getUserAgency()
        if (typeof userId !== "number") {
          const newUser = getDefaultUser(agency.id, agency.currency.id as CURRENCIES)
          defaultDataRef.current = newUser
          setUserData(newUser)
          return
        }
        setIsLoading(true)
        getUserById(userId)
          .then(async u => {
            const fetchedUser: IUserFormData = u as IUserFormData
            if (fetchedUser.agency_id !== agency.id) {
              console.error("User don't have agency")
              setHasLoadingError(true)
              return
            }
            try {
              const roles = await getUserRoles(userId)
              const isUserAdmin = await isAppAdmin(userId, roles)
              setInitialIsAdmin(isUserAdmin)
              setIsAdmin(isUserAdmin)
              if (await isSspUser(userId, roles)) {
                fetchedUser.role = USER_ROLES.SSP_USER
              } else if (await isDspUser(userId, roles)) {
                fetchedUser.role = USER_ROLES.DSP_USER
              } else if (await isDspSale(userId, roles)) {
                fetchedUser.role = USER_ROLES.SALE
              } else {
                fetchedUser.role = USER_ROLES.REPORTS
              }
              setInitialUser(fetchedUser)
              setUserData(fetchedUser)
            } catch (error) {
              console.error("Unable to check is admin", error)
              setHasLoadingError(true)
            }
          })
          .catch(error => {
            console.error(error)
            setHasLoadingError(true)
          })
          .finally(() => setIsLoading(false))
      } catch (error) {
        console.error(error)
        setHasLoadingError(true)
        return
      }
    }, [isInitializating])
    return (
      <FlexBox fullWidth vertical gap sx={{ maxWidth: "100%" }}>
        {hasLoadingError ? (
          <Typography color="error" fontSize=".9rem">
            {translate("user.load.error")}
          </Typography>
        ) : isLoading || !userData ? (
          <LinearLoader />
        ) : (
          <>
            <Formik
              initialValues={userData}
              validate={(values: IUserFormData) => {
                setUserData(values)
                return validateUserFormData(values, otherUsers)
              }}
              onSubmit={async (_values, actions) => {
                if (!hasChanged) {
                  actions.setSubmitting(false)
                  return
                }
                if (isSaving) {
                  return
                }
                setIsSaving(true)
                const setRoleAndAdmin = async (user: IUser | void | undefined | "error") => {
                  let roleHasChanged = false
                  if (user !== "error" && hasAdminChanged) {
                    try {
                      await setUserAppAdmin(user ? user.id : (userId as number), isAdmin)
                      roleHasChanged = true
                      setInitialIsAdmin(isAdmin)
                    } catch (error) {
                      messageSVC.error(translate("user.set.admin.role.error"))
                    }
                  }
                  if (user && user !== "error" && hasRoleChanged) {
                    if (!roleHasChanged) {
                      roleHasChanged = true
                    }
                    let newRoleHasBeenAdded = false
                    try {
                      await addDspRole(user.id, userData.role)
                      newRoleHasBeenAdded = true
                    } catch (error) {
                      if (error !== USER_ROLE_ALREADY_EXISTS_ERROR) {
                        console.error(error, (error as any).message)
                        messageSVC.error(translate("user.set.role.error"))
                      } else {
                        newRoleHasBeenAdded = true
                      }
                    }
                    if (initialUser && newRoleHasBeenAdded) {
                      try {
                        await removeDspRole(user.id, initialUser.role)
                      } catch (error) {
                        if (error !== ROLE_NOT_FOUND_ERROR) {
                          console.error(error)
                          try {
                            await removeDspRole(user.id, userData.role)
                          } catch (err) {
                            console.error(err)
                          }
                          messageSVC.error(translate("user.set.role.error"))
                        }
                      }
                    }
                  }
                  if (typeof user === "object") {
                    if (roleHasChanged && onRoleChange) {
                      onRoleChange(user)
                    }
                    setInitialUser(user as IUserFormData)
                    setUserData(user as IUserFormData)
                    onSave(user)
                  }
                  setIsSaving(false)
                  actions.setSubmitting(false)
                }
                if (isCreation) {
                  const response = await createUser(userData.email, userData)
                  if (response !== "error") {
                    const user = response.user
                    messageSVC.confirmMessage({
                      title: translate("user.password.title"),
                      message: translate("user.password.message"),
                      onSuccess: () => {
                        /* nothing to do */
                      },
                      icon: faTriangleExclamation,
                      children: (
                        <FlexBox
                          jc="end"
                          fullWidth
                          gap=".6rem"
                          sx={{
                            "mt": "1rem",
                            "& .icon": { color: theme => theme.palette.secondary.main },
                            "& .icon:hover": { cursor: "pointer", color: theme => theme.palette.primary.main }
                          }}>
                          <Typography color="textPrimary" fontSize=".9rem">
                            {translate("label.password")}
                          </Typography>
                          <Typography id="user-form-new-user-password" color="textSecondary" fontSize="1rem">
                            {response.clearPassword}
                          </Typography>
                          <FontAwesomeIcon
                            icon={faCopy}
                            onClick={() => copyToClipboard(response.clearPassword)}
                            title={translate("label.copy.clipboard")}
                            className="icon"
                          />
                        </FlexBox>
                      ),
                      hideCancelButton: true
                    })
                    setRoleAndAdmin(user)
                  } else {
                    setRoleAndAdmin(response)
                  }
                } else {
                  messageSVC.confirmMessage({
                    title: translate("user.update.title"),
                    message: translate("user.update.message", userData.email),
                    onSuccess: async () => {
                      if (hasUserChanged || password1 !== "") {
                        const user = await updateUser(
                          userData,
                          userId as number,
                          password1
                            ? {
                                newClearPassword: password1,
                                currentClearPassword: password0,
                                email: initialUser?.email as string
                              }
                            : undefined
                        )
                        if (password1 && user !== "error") {
                          setPassword0("")
                          setPassword1("")
                          setPassword2("")
                        }
                        setRoleAndAdmin(user)
                      } else {
                        setRoleAndAdmin(userData as IUser)
                      }
                    }
                  })
                }
              }}>
              {({ errors, isSubmitting, setFieldValue, setValues, values }) => {
                const canSubmit = !isSubmitting && Object.keys(errors).length === 0 && !passwordError
                return (
                  <Form
                    id={isCreation ? "user-form-creation" : "user-form-edit"}
                    style={{ padding: ".5rem", maxWidth: "100%", width: isTablet ? "100%" : undefined }}>
                    {isSaving ? <TestHiddenData id="user-form-is-saving" /> : <TestHiddenData id="user-form-saved" />}
                    <FlexBox
                      wrap
                      gap
                      vertical
                      fullWidth
                      ai="start"
                      sx={{
                        "maxWidth": "100%",
                        "height": "100%",
                        "minHeight": "100%",
                        "maxHeight": "100%",
                        "& .form-container > *": {
                          width: addImportant(!isTablet ? "20rem" : "100%"),
                          minWidth: addImportant(!isTablet ? "20rem" : "100%"),
                          maxWidth: addImportant(!isTablet ? "20rem" : "100%")
                        },
                        "& .form-container": {
                          width: isTablet ? "100%" : undefined
                        }
                      }}>
                      <FlexBox id={`user-form-saving-${isSaving ? "on" : "off"}`} sx={{ display: "none" }} />
                      <FlexBox wrap gap fullWidth vertical={isTablet} ai="start">
                        <FlexBox vertical gap className="form-container">
                          <FieldText
                            fieldName="email"
                            label={translate("label.email")}
                            value={values.email}
                            error={errors.email}
                            autoFocus
                            disabled={!isCreation}
                            refreshValue={refreshCount}
                            required
                          />
                          <FieldText
                            fieldName="firstname"
                            label={translate("label.firstname")}
                            value={values.firstname as string}
                            error={errors.firstname}
                            disabled={disableUserEdit}
                            refreshValue={refreshCount}
                            required
                          />
                          <FieldText
                            fieldName="lastname"
                            label={translate("label.lastname")}
                            value={values.lastname as string}
                            error={errors.lastname}
                            disabled={disableUserEdit}
                            refreshValue={refreshCount}
                            required
                          />
                          {!isSelfUser && (
                            <YesNotSelect
                              id="user-status"
                              disabled={disableUserRole}
                              value={values.active}
                              onChange={newValue => setFieldValue("active", newValue)}
                              unique
                              mainLabel={translate("label.status")}
                              yesLabel={translate("label.active")}
                              notLabel={translate("label.inactive")}
                            />
                          )}
                          <FieldSelect
                            fieldName="ui_settings.theme"
                            label={translate("admin.ui-configuration.menu.title")}
                            value={
                              values.ui_settings?.theme
                                ? values.ui_settings.theme
                                : (getDefaultTheme() as any).palette.type === UI_THEMES.DARK
                                  ? UI_THEMES.DARK
                                  : UI_THEMES.LIGHT
                            }
                            options={[UI_THEMES.DARK, UI_THEMES.LIGHT]}
                            optionLabelFn={theme => translate(`label.${theme}`)}
                            disabled={disableUserEdit}
                          />
                        </FlexBox>
                        <FlexBox vertical gap className="form-container">
                          <FieldSelect
                            fieldName="ui_settings.distance_unit"
                            label={translate("label.distance-unit")}
                            value={
                              values.ui_settings?.distance_unit
                                ? values.ui_settings.distance_unit
                                : DISTANCE_UNITS.MILES
                            }
                            options={[...ALL_MAP_DISTANCE_UNITS]}
                            optionLabelFn={unit => translate(`label.distance-unit.${unit}`)}
                            disabled={disableUserEdit}
                          />
                          <FieldSelect
                            fieldName="language_id"
                            label={translate("label.language")}
                            value={values.language_id || LOCALES.EN}
                            options={ALL_LOCALES.slice()}
                            optionLabelFn={locale => translate(`label.language.${locale}`)}
                            error={errors.language_id}
                            disabled={disableUserEdit}
                          />
                          <FieldPhone
                            fieldName="phone"
                            defaultCountry={
                              navigator.language.length == 5 ? navigator.language.substring(3, 5).toLowerCase() : "us"
                            }
                            error={errors.phone}
                            value={values.phone || ""}
                            style={{ zIndex: 10 }}
                          />
                          <FieldSelect
                            fieldName="currency_id"
                            label={translate("label.currency")}
                            value={values.currency_id || ALL_CURRENCIES[0]}
                            options={getAvailableCurrencyIds()}
                            optionLabelFn={(currency: string) => currency}
                            error={errors.currency_id}
                            disabled={disableUserEdit}
                          />
                          {!disableUserRole && !isSspUserSession() && (
                            <>
                              <FieldSelect
                                fieldName="role"
                                label={translate("label.role")}
                                value={values.role || "dsp.user"}
                                options={["dsp.user", "dsp.sale", "dsp.reports"]}
                                optionLabelFn={(role: string) => translate(`user.role.${role}`)}
                                error={errors.currency_id}
                                disabled={disableUserEdit}
                              />
                              <Typography color="textSecondary" fontSize=".85rem">
                                {translate(`user.role.${values.role}.description`)}
                              </Typography>
                            </>
                          )}
                          {!disableUserPassword && (
                            <>
                              <FormControl id="password0">
                                <TextField
                                  id="password0-input"
                                  variant="outlined"
                                  error={passwordError !== ""}
                                  label={translate("user.password0")}
                                  size="small"
                                  value={password0}
                                  type="password"
                                  onChange={event => setPassword0(event.target.value)}
                                />
                              </FormControl>
                              <FormControl id="password1">
                                <TextField
                                  id="password1-input"
                                  variant="outlined"
                                  error={passwordError !== ""}
                                  label={translate("user.password1")}
                                  size="small"
                                  value={password1}
                                  type="password"
                                  onChange={event => setPassword1(event.target.value)}
                                />
                              </FormControl>
                              <FormControl id="password2">
                                <TextField
                                  id="password2-input"
                                  variant="outlined"
                                  error={passwordError !== ""}
                                  label={translate("user.password2")}
                                  size="small"
                                  value={password2}
                                  type="password"
                                  onChange={event => setPassword2(event.target.value)}
                                />
                              </FormControl>
                              {passwordError && (
                                <Typography id="password-error-message" color="error" fontSize=".8rem">
                                  {translate(
                                    passwordError === "different"
                                      ? "user.password.error.different"
                                      : passwordError === "actual-invalid"
                                        ? "user.password.actual.missing"
                                        : "user.password.error.invalid"
                                  )}
                                </Typography>
                              )}
                            </>
                          )}
                          <FormControlLabel
                            id="is-admin-checkbox"
                            disabled={disableUserRole}
                            control={
                              <Checkbox
                                defaultChecked={isAdmin}
                                value={isAdmin}
                                onChange={(_event, checked) => setIsAdmin(checked)}
                              />
                            }
                            label={translate("label.is.admin")}
                            sx={{ "width": addImportant("100%"), "& span.MuiTypography-root": { fontSize: ".9rem" } }}
                          />
                        </FlexBox>
                      </FlexBox>
                    </FlexBox>
                    {!areButtonsDisabled && (
                      <FlexBox gap fullWidth ai="end" jc="end" sx={{ mt: "2rem", width: "100%" }}>
                        <Button
                          id="self-user-form-button-cancel"
                          variant="outlined"
                          onClick={() => {
                            if (onCancel) {
                              onCancel()
                            } else {
                              if (hasUserChanged) {
                                setValues(initialUser as IUserFormData, true)
                                setRefreshCount(refreshCount + 1)
                              }
                              if (hasAdminChanged) {
                                setIsAdmin(initialIsAdmin)
                              }
                              if (password1 || password2) {
                                setPassword0("")
                                setPassword1("")
                                setPassword2("")
                              }
                            }
                          }}
                          disabled={isSaving || (!onCancel && !hasChanged)}>
                          {translate("button.cancel")}
                        </Button>
                        {!(disableUserEdit && disableUserPassword && disableUserRole) && (
                          <Button
                            id="self-user-form-button-save"
                            variant="contained"
                            startIcon={
                              isSubmitting ? (
                                <CircularProgress sx={{ color: theme => theme.palette.primary.main }} size="1.5rem" />
                              ) : (
                                <FontAwesomeIcon icon={faClone} />
                              )
                            }
                            type="submit"
                            disabled={!hasChanged || !canSubmit || isSaving}>
                            {translate(isCreation ? "button.create" : "button.update")}
                          </Button>
                        )}
                      </FlexBox>
                    )}
                  </Form>
                )
              }}
            </Formik>
          </>
        )}
      </FlexBox>
    )
  }
)

export const UserForm = connect(
  (state: IRootState): IUserFormConnectProps => ({
    isInitializating: state.app.isInitializating,
    isTablet: isTabletScreen(state),
    isLoggedIn: state.user.token !== EMPTY_TOKEN_VALUE,
    selfUser: state.user.user
  })
)(UserFormFn)
